import type React from "react";
import { DealFileCard } from "~/components/common/deals/show/files/DealFileCard";
import { LeaseContractFileCard } from "~/components/common/deals/show/files/LeaseContractFileCard";
import { LeasingInspectionFileCard } from "~/components/common/deals/show/files/LeasingInspectionFileCard";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useKtLeasings } from "~/hooks/vendors/useKtLeasings";
import { useSessions } from "~/hooks/vendors/useSessions";
import type { NskResponse } from "~/models/Api";
import type { Deal } from "~/models/Deal";
import { canShowCheckFileCards } from "~/models/Deal";
import type {
	DealUploadFile,
	DealUploadFileUpdateParam
} from "~/models/DealUploadFile";
import {
	getOriginDealVendor,
	isOriginVendor
} from "~/models/DealVendorCompany";
import {
	getInspectionFilePath,
	getLeaseContractFilePath
} from "~/utils/api/vendors/Deal";
import { create as uploadCompletionCheckFile } from "~/utils/api/vendors/deals/DealCompletionCheckFile";
import { create as uploadEquipmentCheckFile } from "~/utils/api/vendors/deals/DealEquipmentCheckFile";
import { create as uploadMemorandumFile } from "~/utils/api/vendors/deals/DealMemorandumFile";
import {
	create as postOtherFile,
	update as putOtherFile
} from "~/utils/api/vendors/deals/DealOtherFile";
import {
	getDownloadPath,
	create as postReceiptFile
} from "~/utils/api/vendors/deals/DealReceiptFile";
import {
	destroy as destroyFile,
	getFilePath as getVendorFilePath
} from "~/utils/api/vendors/deals/DealUploadedFile";

function VendorInspectionFileSection({ deal }: { deal: Deal }) {
	const { data: leasing, isLoading } = useKtLeasings(String(deal.leasingId!));
	if (isLoading) return null;
	return (
		<LeasingInspectionFileCard
			deal={deal}
			hasInspectionFile={leasing?.hasInspectionFile ?? false}
			inspectionFilePath={getInspectionFilePath(deal.id)}
		/>
	);
}

export const DealFileListVendor: React.FC = () => {
	const id = useIdQueryHandler();
	const { data: deal, mutate: mutateDeal } = useDeals().show(Number(id));
	const { data: session } = useSessions().index();
	if (deal === null || session === null) {
		return null;
	}
	const showTenant = getOriginDealVendor(deal)?.showTenant ?? false;
	const handleGetFilePath = (file: DealUploadFile): string =>
		getVendorFilePath(deal.id, file);
	const handleMutate = async (): Promise<void> => {
		await mutateDeal?.();
	};
	const handleDestroyFile = async (
		file: DealUploadFile
	): Promise<NskResponse> => destroyFile(deal.id, file.id);
	const handleUploadReceipt = async (file: File): Promise<NskResponse> =>
		postReceiptFile(file, deal.id);
	const createPdf = async (): Promise<NskResponse> =>
		postReceiptFile(null, deal.id);
	const downloadExcel = (): void => {
		const path = getDownloadPath(deal.id);
		window.open(path);
	};
	const handleUploadOther = async (file: File): Promise<NskResponse> =>
		postOtherFile(file, deal.id);
	const handleUpdateOther = async (
		dealOtherParam: DealUploadFileUpdateParam
	): Promise<NskResponse> => putOtherFile(dealOtherParam, deal.id);
	const handleUploadMemorandum = async (file: File): Promise<NskResponse> =>
		uploadMemorandumFile(file, deal.id);
	const handleUploadCompletionCheck = async (
		file: File
	): Promise<NskResponse> => uploadCompletionCheckFile(file, deal.id);
	const handleUploadEquipmentCheck = async (file: File): Promise<NskResponse> =>
		uploadEquipmentCheckFile(file, deal.id);
	return (
		<>
			{showTenant ? (
				<LeaseContractFileCard
					deal={deal}
					handleGetFile={getLeaseContractFilePath}
					handleMutate={handleMutate}
					isVendor
				/>
			) : null}
			{showTenant && deal.leasingId !== null ? (
				<VendorInspectionFileSection deal={deal} />
			) : null}
			{showTenant ? (
				<DealFileCard
					deal={deal}
					files={deal.dealMemorandumFiles}
					fileType="賃借人同意書"
					handleDestroy={handleDestroyFile}
					handleGetFilePath={handleGetFilePath}
					handleMutate={handleMutate}
					handleUpload={handleUploadMemorandum}
					isVendor
					vendorSession={session}
				/>
			) : null}
			{canShowCheckFileCards(deal) && (
				<DealFileCard
					deal={deal}
					files={deal.dealEquipmentCheckFiles}
					fileType="設備チェック表"
					handleDestroy={handleDestroyFile}
					handleGetFilePath={handleGetFilePath}
					handleMutate={handleMutate}
					handleUpload={handleUploadEquipmentCheck}
					isVendor
					vendorSession={session}
				/>
			)}
			{isOriginVendor(deal, session.vendorCompany.id) && (
				<DealFileCard
					createPdf={createPdf}
					deal={deal}
					description="登録されている工事発注書・請書は必要に応じてお手続きにご利用ください"
					downloadExcel={downloadExcel}
					files={deal.dealReceiptFiles}
					fileType="工事発注書・請書"
					handleDestroy={handleDestroyFile}
					handleGetFilePath={handleGetFilePath}
					handleMutate={handleMutate}
					handleUpload={handleUploadReceipt}
					isVendor
					vendorSession={session}
				/>
			)}
			{canShowCheckFileCards(deal) && (
				<DealFileCard
					deal={deal}
					files={deal.dealCompletionCheckFiles}
					fileType="完了チェックシート"
					handleDestroy={handleDestroyFile}
					handleGetFilePath={handleGetFilePath}
					handleMutate={handleMutate}
					handleUpload={handleUploadCompletionCheck}
					isVendor
					vendorSession={session}
				/>
			)}
			<DealFileCard
				deal={deal}
				description="管理会社とファイルが共有されます"
				files={deal.dealOtherFiles}
				fileType="その他ファイル"
				handleDestroy={handleDestroyFile}
				handleGetFilePath={handleGetFilePath}
				handleMutate={handleMutate}
				handleUpdate={handleUpdateOther}
				handleUpload={handleUploadOther}
				isVendor
				vendorSession={session}
			/>
		</>
	);
};
